import React, { useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Expand } from "lucide-react";
import PageShell from "../components/PageShell";
import Lightbox from "../components/Lightbox";
import { getProjectBySlug, getAdjacentProjects } from "../data/projects";

// Natural-aspect image card — no forced cropping, shows the photograph in full.
const NaturalImage = ({ src, alt, testId, rounded = "rounded-[24px]", onClick, clickable = false }) => (
  <motion.figure
    initial={{ opacity: 0, y: 12 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.7, ease: "easeOut" }}
    data-testid={testId}
    onClick={onClick}
    className={`relative overflow-hidden ${rounded} border border-white/70 shadow-[0_10px_40px_rgba(198,226,255,0.25)] bg-white/60 group ${clickable ? "cursor-zoom-in" : ""}`}
  >
    <img
      src={src}
      alt={alt}
      loading="lazy"
      className={`block w-full h-auto ${clickable ? "transition-transform duration-[900ms] ease-out group-hover:scale-[1.02]" : ""}`}
    />
    {clickable && (
      <div className="pointer-events-none absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
        <div className="glass-strong rounded-full p-2">
          <Expand size={14} className="text-slate-700 dark:text-slate-200" />
        </div>
      </div>
    )}
  </motion.figure>
);

const ProjectDetail = () => {
  const { slug } = useParams();
  const project = getProjectBySlug(slug);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  if (!project) return <Navigate to="/portfolio" replace />;
  const { prev, next } = getAdjacentProjects(slug);

  // All images: hero first, then the gallery — so clicking the hero opens
  // the lightbox at index 0 and clicking gallery photos opens at their index+1.
  const allImages = [{ src: project.hero }, ...project.gallery];
  const openAt = (i) => setLightboxIndex(i);
  const close = () => setLightboxIndex(null);

  return (
    <PageShell testId="page-project">
      {/* Header */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-4 pb-10">
        <Link
          to="/portfolio"
          data-testid="project-back-link"
          className="inline-flex items-center gap-2 font-manrope text-xs tracking-[0.2em] uppercase text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:text-slate-100 transition"
        >
          <ArrowLeft size={14} /> Back to Portfolio
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="mt-8 grid grid-cols-1 md:grid-cols-12 gap-8 items-end"
        >
          <div className="md:col-span-8">
            <div className="font-manrope text-[10px] tracking-[0.3em] uppercase text-[#A8D2FA] dark:text-white mb-4">
              {project.category} · {project.date}
            </div>
            <h1
              data-testid="project-title"
              className="font-fraunces text-5xl md:text-7xl font-light text-slate-900 dark:text-slate-100 leading-[1.02] text-balance"
            >
              {project.title}
            </h1>
          </div>
          <div className="md:col-span-4">
            <p className="font-manrope text-slate-600 dark:text-slate-300 leading-relaxed">
              {project.description}
            </p>
          </div>
        </motion.div>
      </section>

      {/* Hero image — natural aspect, uncropped, clickable */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-16">
        <NaturalImage
          src={project.hero}
          alt={`${project.title} — hero`}
          rounded="rounded-[36px]"
          testId="project-hero-image"
          clickable
          onClick={() => openAt(0)}
        />
      </section>

      {/* Long-form story — rendered only when the project has one */}
      {project.longDescription && (
        <section
          className="max-w-3xl mx-auto px-6 md:px-8 pb-20"
          data-testid="project-story"
        >
          <div className="font-manrope text-[10px] tracking-[0.3em] uppercase text-[#A8D2FA] dark:text-white mb-6">
            The story
          </div>
          <div className="space-y-6 font-manrope text-base md:text-lg leading-[1.85] text-slate-700 dark:text-slate-200">
            {project.longDescription.map((para, i) => (
              <p key={i} className={i === 0 ? "first-letter:font-fraunces first-letter:text-5xl first-letter:font-light first-letter:mr-2 first-letter:float-left first-letter:leading-[0.9] first-letter:mt-1 first-letter:text-slate-900 dark:first-letter:text-white" : ""}>
                {para}
              </p>
            ))}
          </div>

          {project.coreMessage && (
            <figure className="mt-14 relative">
              <div className="font-manrope text-[10px] tracking-[0.3em] uppercase text-[#A8D2FA] dark:text-white mb-4">
                Core message
              </div>
              <blockquote className="font-fraunces text-2xl md:text-3xl italic font-light text-slate-900 dark:text-slate-100 leading-[1.35] text-balance border-l-2 border-[#C6E2FF] pl-6 md:pl-8">
                &ldquo;{project.coreMessage}&rdquo;
              </blockquote>
            </figure>
          )}
        </section>
      )}

      {/* Gallery — masonry, images shown at full natural scale */}
      <section
        className="max-w-7xl mx-auto px-6 md:px-12 pb-24"
        data-testid="project-gallery"
      >
        <div className="columns-1 md:columns-2 gap-6 space-y-6 [&>*]:break-inside-avoid">
          {project.gallery.map((image, i) => (
            <NaturalImage
              key={i}
              src={image.src}
              alt={`${project.title} — frame ${i + 1}`}
              testId={`gallery-image-${i}`}
              clickable
              onClick={() => openAt(i + 1)}
            />
          ))}
        </div>
      </section>

      <Lightbox
        images={allImages}
        index={lightboxIndex}
        onClose={close}
        onNav={setLightboxIndex}
      />

      {/* Prev / Next */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-16">
        <div className="glass rounded-[32px] p-6 md:p-8 grid grid-cols-1 md:grid-cols-2 gap-6">
          {prev && (
            <Link
              to={`/portfolio/${prev.slug}`}
              data-testid="project-prev"
              className="group flex items-center gap-4 rounded-2xl p-4 hover:bg-white/60 transition"
            >
              <ArrowLeft size={18} className="text-slate-500 dark:text-slate-400 group-hover:-translate-x-1 transition-transform" />
              <div>
                <div className="font-manrope text-[10px] tracking-[0.3em] uppercase text-slate-400 dark:text-slate-500 dark:text-slate-400">
                  Previous
                </div>
                <div className="font-fraunces text-xl text-slate-900 dark:text-slate-100 mt-0.5">
                  {prev.title}
                </div>
              </div>
            </Link>
          )}
          {next && (
            <Link
              to={`/portfolio/${next.slug}`}
              data-testid="project-next"
              className="group flex md:justify-end items-center gap-4 rounded-2xl p-4 hover:bg-white/60 transition md:text-right"
            >
              <div>
                <div className="font-manrope text-[10px] tracking-[0.3em] uppercase text-slate-400 dark:text-slate-500 dark:text-slate-400">
                  Next
                </div>
                <div className="font-fraunces text-xl text-slate-900 dark:text-slate-100 mt-0.5">
                  {next.title}
                </div>
              </div>
              <ArrowRight size={18} className="text-slate-500 dark:text-slate-400 group-hover:translate-x-1 transition-transform" />
            </Link>
          )}
        </div>
      </section>
    </PageShell>
  );
};

export default ProjectDetail;
